import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Grid
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import MenuLayout from "../app/MenuLayout";
import { CouponTemplateFormDto, CodeType, ActivationType, ValueType, CouponStatusAfterIssuance, DesignType, mapCouponTemplateFormToCreateRequest, CouponTemplateApiResponse, mapCouponTemplateApiResponseToForm } from "../../store/models/coupon/CouponManagementDto";
import { Api } from "../../api/Api";
import { thunkCreateSuccessNotification, thunkCreateErrorNotification } from "../../store/actions/NotificationActions";
import { thunkCreateCouponTemplate, thunkUpdateCouponTemplate, thunkGetCouponTemplateByNumber } from "../../store/actions/CouponActions";
import CouponBasicInfoStep from "./steps/CouponBasicInfoStep";
import CouponRewardValueStep from "./steps/CouponRewardValueStep";
import CouponTypeStep from "./steps/CouponTypeStep";
import CouponValidityStep from "./steps/CouponValidityStep";
import CouponOperationalUnitsStep from "./steps/CouponOperationalUnitsStep";
import CouponCodeGenerationStep from "./steps/CouponCodeGenerationStep";
import CouponDesignStep from "./steps/CouponDesignStep";

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      thunkCreateSuccessNotification,
      thunkCreateErrorNotification,
      thunkCreateCouponTemplate,
      thunkUpdateCouponTemplate,
      thunkGetCouponTemplateByNumber,
    },
    dispatch
  );

const connector = connect(null, mapDispatchToProps);
type CouponFormPageProps = ConnectedProps<typeof connector>;

const CouponFormPage = ({
  thunkCreateSuccessNotification,
  thunkCreateErrorNotification,
  thunkCreateCouponTemplate,
  thunkUpdateCouponTemplate,
  thunkGetCouponTemplateByNumber
}: CouponFormPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { couponId } = useParams<{ couponId: string }>();
  const [isEditMode, setIsEditMode] = useState(false);
  
  const [formData, setFormData] = useState<CouponTemplateFormDto>({
    name: '',
    // Reward and Value
    valueType: ValueType.PERCENTAGE,
    value: '',
    minimumPurchaseAmount: undefined,
    // Coupon Type
    activationType: ActivationType.FREE,
    requiredPoints: undefined,
    initialCouponStatus: CouponStatusAfterIssuance.ACTIVE,
    // Validity
    validityType: 'DAYS_FROM_ACTIVATION',
    validityPeriodDays: undefined,
    // Redeemable At
    operationalUnitNumbers: [],
    // Code Generation
    codeType: CodeType.GENERIC,
    codePrefix: '',
    codeLength: undefined,
    couponCode: undefined,
    // Design
    designType: DesignType.NONE,
    displayedCouponName: undefined,
    description: undefined,
    couponImage: undefined,
    infoTextForEndUsers: undefined,
    // Legacy
    uploadFileName: '',
    eligibilityType: undefined,
    eligibilityNumbers: [],
  });

  // Load existing coupon data in edit mode
  useEffect(() => {
    const loadExistingCoupon = async (couponNumber: string) => {
      try {
        const couponData = await Api.getCouponTemplateByNumber(couponNumber) as CouponTemplateApiResponse;
        const formDataFromApi = mapCouponTemplateApiResponseToForm(couponData);
        // Ensure initialCouponStatus has a value
        if (!formDataFromApi.initialCouponStatus) {
          formDataFromApi.initialCouponStatus = CouponStatusAfterIssuance.ACTIVE;
        }
        setFormData(formDataFromApi);
      } catch (error) {
        console.error('Failed to load coupon:', error);
        thunkCreateErrorNotification(
          t("coupon.form.loadError", "Failed to load coupon data"),
          error
        );
      }
    };

    if (couponId) {
      setIsEditMode(true);
      loadExistingCoupon(couponId);
    }
  }, [couponId, thunkCreateErrorNotification, t]);

  const handleCancel = () => {
    navigate(-1);
  };

  const isValidAlphanumeric = (value: string): boolean => {
    // Only allow letters (a-z, A-Z) and numbers (0-9), no spaces or special characters
    const alphanumericPattern = /^[a-zA-Z0-9]*$/;
    return alphanumericPattern.test(value);
  };

  // Extracted validation functions to reduce complexity
  const validateBasicFields = (formData: CouponTemplateFormDto): string[] => {
    const errors: string[] = [];

    if (!formData.name?.trim()) {
      errors.push("Coupon name is required");
    }

    // Validate validity period if provided (field validation happens in the step)
    if (formData.validityPeriodDays !== undefined && formData.validityPeriodDays <= 0) {
      errors.push("Validity period must be positive");
    }

    return errors;
  };

  const validateValueFields = (formData: CouponTemplateFormDto): string[] => {
    const errors: string[] = [];
    const requiresValue = [ValueType.PERCENTAGE, ValueType.FIXED_AMOUNT].includes(formData.valueType);
    
    if (requiresValue && !formData.value?.trim()) {
      errors.push("Coupon value is required for this value type");
    }

    return errors;
  };

  const validateCodeFields = (formData: CouponTemplateFormDto): string[] => {
    const errors: string[] = [];

    if (formData.codeType === CodeType.INDIVIDUAL_GENERATED) {
      // Code prefix is optional, but if provided, must be alphanumeric
      if (formData.codePrefix && formData.codePrefix.trim() && !isValidAlphanumeric(formData.codePrefix)) {
        errors.push("Code prefix can only contain letters and numbers");
      }

      if (!formData.codeLength || formData.codeLength < 4 || formData.codeLength > 20) {
        errors.push("Code length must be between 4 and 20 characters");
      }
    } else if (formData.codeType === CodeType.GENERIC) {
      if (!formData.couponCode?.trim()) {
        errors.push("Generic code is required");
      } else if (!isValidAlphanumeric(formData.couponCode)) {
        errors.push("Coupon code can only contain letters and numbers");
      }
    }

    return errors;
  };

  const validateActivationFields = (formData: CouponTemplateFormDto): string[] => {
    const errors: string[] = [];

    if (formData.activationType === ActivationType.POINTS && (!formData.requiredPoints || formData.requiredPoints <= 0)) {
      errors.push("Required points must be positive when activation requires points");
    }

    return errors;
  };

  const validateFormData = (formData: CouponTemplateFormDto): string[] => {
    return [
      ...validateBasicFields(formData),
      ...validateValueFields(formData),
      ...validateCodeFields(formData),
      ...validateActivationFields(formData)
    ];
  };

  const handleSave = async () => {
    try {
      // Validate form data first
      const validationErrors = validateFormData(formData);
      if (validationErrors.length > 0) {
        thunkCreateErrorNotification(
          "Validation Error",
          new Error(validationErrors.join('. '))
        );
        return;
      }

      // Use the new mapping function instead of the old transform function
      const couponRequest = mapCouponTemplateFormToCreateRequest(formData);

      let success = false;
      if (isEditMode && couponId) {
        success = await (thunkUpdateCouponTemplate(couponId, couponRequest) as Promise<boolean>);
      } else {
        success = await (thunkCreateCouponTemplate(couponRequest) as Promise<boolean>);
      }
      
      if (success) {
        navigate(-1);
      }
    } catch (error) {
      console.error('Failed to save coupon:', error);
      thunkCreateErrorNotification(
        isEditMode 
          ? t("coupon.form.updateError", "Failed to update coupon")
          : t("coupon.form.createError", "Failed to create coupon"),
        error
      );
    }
  };

  const updateFormData = (updates: Partial<CouponTemplateFormDto>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  return (
    <MenuLayout headline={isEditMode ? t("coupon.editCoupon") : t("coupon.newCoupon")}>
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={4}>
          {/* Coupon Name Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
              <CouponBasicInfoStep
                formData={formData}
                updateFormData={updateFormData}
              />
            </Paper>
          </Grid>

          {/* Reward and Value Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
              <CouponRewardValueStep
                formData={formData}
                updateFormData={updateFormData}
              />
            </Paper>
          </Grid>

          {/* Coupon Type Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
              <CouponTypeStep
                formData={formData}
                updateFormData={updateFormData}
              />
            </Paper>
          </Grid>

          {/* Validity Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
              <CouponValidityStep
                formData={formData}
                updateFormData={updateFormData}
              />
            </Paper>
          </Grid>

          {/* Redeemable At Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
              <CouponOperationalUnitsStep
                formData={formData}
                updateFormData={updateFormData}
              />
            </Paper>
          </Grid>

          {/* Code Generation Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
              <CouponCodeGenerationStep
                formData={formData}
                updateFormData={updateFormData}
              />
            </Paper>
          </Grid>

          {/* Design Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
              <CouponDesignStep
                formData={formData}
                updateFormData={updateFormData}
              />
            </Paper>
          </Grid>

          {/* Action Buttons */}
          <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              color="secondary"
              variant="contained"
              onClick={handleCancel}
              sx={{ m: theme => theme.spacing(0, 0, 2, 2) }}
            >
              {t("coupon.cancel")}
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={handleSave}
              sx={{ m: theme => theme.spacing(0, 0, 2, 2) }}
            >
              {isEditMode ? t("coupon.update") : t("coupon.save")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </MenuLayout>
  );
};

export default connector(CouponFormPage);